import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useState,
} from "react";
import { useFormik } from "formik";
import { useNavigate } from "react-router-dom";
import api from "../../../config/URL";
import { toast } from "react-toastify";
import * as Yup from "yup";
import fetchAllCentersWithIds from "../../List/CenterList";
import MultiSelect from "react-select";
import CustomDatePicker from "../../CustomDatePicker";

const validationSchema = Yup.object().shape({
  employee: Yup.string().required("*Employee is required"),
  userContractAddress: Yup.string().required("*Address is required"),
  startDateOfEmployment: Yup.string().required(
    "*StartDate Of Employment is required"
  ),
  userContractStartDate: Yup.string().required(
    "*Start Date Of Contract is required"
  ),
  workingDays: Yup.array()
    .min(1, "*Working days are required")
    .required("*Working days are required"),
  userContractSalary: Yup.number()
    .typeError("*Salary Must be numbers")
    .required("*Salary is required"),
  allowance: Yup.number().typeError("*Allowance Must be numbers").notRequired(),
});

const StaffContractEdit = forwardRef(
  ({ formData, setLoadIndicators, setFormData }, ref) => {
    const navigate = useNavigate();
    const userName = localStorage.getItem("userName");
    const [centerData, setCenterData] = useState(null);
    const [selectedCenters, setSelectedCenters] = useState([]);
    const [employerData, setEmployerData] = useState(null);
    const [datas, setDatas] = useState();
    const [workingDays, setWorkingDays] = useState();

    const formik = useFormik({
      initialValues: {
        employer: formData.employer || "",
        employee: formData.teacherName || "",
        uen: formData.uen || "",
        addressOfEmployment: formData.addressOfEmployment || "",
        detailsEmployee: formData.detailsEmployee || "",
        nric: formData.nric || "",
        userContractAddress: formData.address || "",
        jobTitle: formData.jobTitle || "",
        mainDuties: formData.mainDuties || "",
        startDateOfEmployment: formData.startDate || "",
        training: formData.training || "",
        allowance: formData.allowance || "",
        userContractStartDate: formData.startDate || "",
        contactPeriod: formData.contactPeriod || "",
        probation: formData.probation || "",
        workingDays: formData.workingDays || "",
        userContractSalary: formData.salary || "",
        salaryStartDate: formData.effectiveDate || "",
        userContractEndDate: formData.endDate || "",
        payNow: formData.payNow || "",
        internetBanking: formData.internetBanking || "",
        contractDate: formData.contractDate || "",
        terminationNotice: formData.terminationNotice || "",
        updatedBy: userName,
      },
      validationSchema: validationSchema,
      onSubmit: async (values) => {
        setLoadIndicators(true);
        values.updatedBy = userName;
        try {
          if (values.contractId !== null) {
            const response = await api.put(
              `/updateUserContractCreation/${values.contractId}`,
              values,
              {
                headers: {
                  "Content-Type": "application/json",
                },
              }
            );
            if (response.status === 200) {
              toast.success("Staff Contract Updated Successfully");
              setFormData((prv) => ({ ...prv, ...values }));
              navigate("/staff");
            } else {
              toast.error(response.data.message);
            }
          } else {
            const response = await api.post(
              `/createUserContractCreation/${formData.staff_id}`,
              values,
              {
                headers: {
                  "Content-Type": "application/json",
                },
              }
            );
            if (response.status === 201) {
              toast.success(response.data.message);
              setFormData((prv) => ({ ...prv, ...values }));
              navigate("/staff");
            } else {
              toast.error(response.data.message);
            }
          }
        } catch (error) {
          toast.error(error);
        } finally {
          setLoadIndicators(false);
        }
      },
      validateOnChange: false, // Enable validation on change
      validateOnBlur: true, // Enable validation on blur
    });

    // Function to scroll to the first error field
    const scrollToError = (errors) => {
      const errorField = Object.keys(errors)[0]; // Get the first error field
      const errorElement = document.querySelector(`[name="${errorField}"]`); // Find the DOM element
      if (errorElement) {
        errorElement.scrollIntoView({ behavior: "smooth", block: "center" });
        errorElement.focus(); // Set focus to the error element
      }
    };

    // Watch for form submit and validation errors
    useEffect(() => {
      if (formik.submitCount > 0 && Object.keys(formik.errors).length > 0) {
        scrollToError(formik.errors);
      }
    }, [formik.submitCount, formik.errors]);

    const fetchData = async () => {
      try {
        const centerData = await fetchAllCentersWithIds();
        setCenterData(centerData);
      } catch (error) {
        toast.error(error);
      }
    };

    const getContractData = async () => {
      try {
        const response = await api.get(`/getAllUserById/${formData.staff_id}`);
        const userData = response.data.userAccountInfo[0];
        setEmployerData(userData.centers);
        setWorkingDays(userData.workingDays);

        // Determine center IDs to use
        let employerIds = [];
        if (formData.centerIds && formData.centerIds.length > 0) {
          employerIds = formData.centerIds;
        } else if (userData.centers && userData.centers.length > 0) {
          employerIds = userData.centers.map((center) => center.id);
        }

        // Set employer field
        formik.setFieldValue("employer", employerIds);

        // Fetch UEN and address if employer IDs exist
        if (employerIds.length > 0) {
          await getData(employerIds);
        }

        // Handle contract data if it exists
        if (
          response.data.userContractCreationModels &&
          response.data.userContractCreationModels.length > 0
        ) {
          const contractData = response.data.userContractCreationModels[0];
          setDatas(contractData);

          formik.setValues({
            ...contractData,
            employer: employerIds,
            uen: formData.uen || response.data.uen || "",
            addressOfEmployment:
              formData.addressOfEmployment ||
              response.data.addressOfEmployment ||
              "",
            employee: formData.teacherName || response.data.teacherName || "",
            userContractAddress:
              formData.address ||
              response.data.userContactInfo[0].address ||
              "",
            startDateOfEmployment:
              contractData.startDateOfEmployment?.substring(0, 10) || "",
            userContractStartDate:
              contractData.userContractStartDate?.substring(0, 10) || "",
            userContractEndDate:
              contractData.userContractEndDate?.substring(0, 10) || "",
            contractDate: contractData.contractDate?.substring(0, 10) || "",
            salaryStartDate:
              contractData.salaryStartDate?.substring(0, 10) || "",
            workingDays: formData.workingDays || userData.workingDays || [],
            userContractSalary:
              formData.salary ||
              response.data.userSalaryCreationModels[0]?.salary ||
              "",
            contractId: contractData.id,
          });
        } else {
          formik.setValues({
            ...formik.values,
            employer: employerIds,
            employee: formData.teacherName || response.data.teacherName || "",
            uen: formData.uen || response.data.uen || "",
            addressOfEmployment:
              formData.addressOfEmployment ||
              response.data.addressOfEmployment ||
              "",
            userContractAddress:
              formData.address ||
              response.data.userContactInfo[0].address ||
              "",
            startDateOfEmployment: userData.startDate || "",
            userContractStartDate: userData.startDate || "",
            workingDays: userData.workingDays || [],
            userContractSalary:
              response.data.userSalaryCreationModels[0]?.salary || "",
            contractDate: userData.startDate || "",
            contractId: null,
          });
        }
      } catch (error) {
        console.error("Error Fetching Data:", error);
        toast.error("Error Fetching Contract Data");
      }
    };

    const getData = async (centerIds) => {
      try {
        if (centerIds && centerIds.length > 0) {
          const response = await api.get(
            `/getConcatenatedCenters?centerIds=${centerIds.join(",")}`
          );
          formik.setFieldValue("uen", response.data.uen);
          formik.setFieldValue(
            "addressOfEmployment",
            response.data.addressOfEmployment
          );
        } else {
          formik.setFieldValue("uen", "");
          formik.setFieldValue("addressOfEmployment", "");
        }
      } catch (error) {
        toast.error("Error Fetching Center Info");
      }
    };

    const centerOptions =
      centerData?.map((center) => ({
        label: center.centerNames || center.centerName,
        value: center.id,
      })) || [];

    // Initialize data fetching
    useEffect(() => {
      fetchData();
      getContractData();
      window.scrollTo({ top: 0, left: 0, behavior: "instant" });
    }, []);

    const calculateContractPeriod = (startDate, endDate) => {
      if (!startDate || !endDate) {
        formik.setFieldValue("contactPeriod", "");
        return;
      }

      const start = new Date(startDate);
      const end = new Date(endDate);

      // Validate end date is after start date
      if (end < start) {
        formik.setFieldError(
          "userContractEndDate",
          "End date must be after start date"
        );
        formik.setFieldValue("contactPeriod", "");
        return;
      } else {
        formik.setFieldError("userContractEndDate", undefined);
      }

      let years = end.getFullYear() - start.getFullYear();
      let months = end.getMonth() - start.getMonth();
      let days = end.getDate() - start.getDate();

      if (days < 0) {
        months -= 1;
        days += new Date(
          start.getFullYear(),
          start.getMonth() + 1,
          0
        ).getDate();
      }

      if (months < 0) {
        years -= 1;
        months += 12;
      }

      const contractPeriod = `${years} years, ${months} months, ${days} days`;
      formik.setFieldValue("contactPeriod", contractPeriod);
    };

    // Update contract period when either date changes
    useEffect(() => {
      calculateContractPeriod(
        formik.values.userContractStartDate,
        formik.values.userContractEndDate
      );
    }, [
      formik.values.userContractStartDate,
      formik.values.userContractEndDate,
    ]);

    useEffect(() => {
      if (centerData && (formData.centerIds || employerData)) {
        let employerIds = [];
        if (formData.centerIds && formData.centerIds.length > 0) {
          employerIds = formData.centerIds.filter((id) =>
            centerData.some((center) => center.id === id)
          );
        } else if (employerData && employerData.length > 0) {
          employerIds = employerData
            .map((center) => center.id)
            .filter((id) => centerData.some((center) => center.id === id));
        }

        formik.setFieldValue("employer", employerIds);
        setSelectedCenters(
          centerOptions.filter((option) => employerIds.includes(option.value))
        );

        if (employerIds.length > 0) {
          getData(employerIds);
        }
      }
    }, [centerData, formData.centerIds, employerData]);

    useImperativeHandle(ref, () => ({
      staffContractEdit: formik.handleSubmit,
    }));

    return (
      <form
        onSubmit={formik.handleSubmit}
        onKeyDown={(e) => {
          if (e.key === "Enter" && !formik.isSubmitting) {
            e.preventDefault(); // Prevent default form submission
          }
        }}
      >
        <div className="container-fluid">
          <p className="headColor my-4">Contract Information</p>
          <div className="container-fluid mt-5" style={{ minHeight: "95vh" }}>
            <span className="mt-3 fw-bold">Details of EMPLOYER</span>
            <div className="row mt-4">
              <div className="col-md-6 col-12 mb-2 mt-3">
                <label>
                  Employer<span className="text-danger">*</span>
                </label>
                <MultiSelect
                  options={centerOptions}
                  value={selectedCenters}
                  onChange={(selected) => {
                    const selectedIds = selected.map((option) => option.value);
                    setSelectedCenters(selected);
                    formik.setFieldValue("employer", selectedIds);
                    getData(selectedIds);
                  }}
                  onBlur={() => formik.setFieldTouched("employer", true)}
                  isMulti
                  labelledBy="Select Employers"
                  menuPlacement="top"
                  className={`form-multi-select ${
                    formik.touched.employer && formik.errors.employer
                      ? "is-invalid"
                      : ""
                  }`}
                />

                {formik.touched.employer && formik.errors.employer && (
                  <div className="invalid-feedback">
                    {formik.errors.employer}
                  </div>
                )}
              </div>
              <div className="col-md-6 col-12 mb-2 mt-3">
                <label>UEN</label>
                <input
                  type="text"
                  className="form-control"
                  name="uen"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.uen}
                  readOnly
                />
              </div>
            </div>
            <div className="col-md-12 col-12 mb-2 mt-3 ">
              <label>Address of Employment</label>
              <textarea
                type="text"
                rows={5}
                className="form-control"
                name="addressOfEmployment"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.addressOfEmployment}
                readOnly
              />
            </div>
            <div className="row mt-3 ">
              <span className="mt-3 fw-bold ">Details of EMPLOYEE</span>
              <div className="col-md-6 col-12 mb-2 mt-3">
                <label>Employee</label>
                <span className="text-danger">*</span>
                <input
                  type="text"
                  className="form-control"
                  name="employee"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.employee}
                  readOnly
                />
                {formik.touched.employee && formik.errors.employee && (
                  <div className="error text-danger ">
                    <small>{formik.errors.employee}</small>
                  </div>
                )}
              </div>
              <div className="col-md-6 col-12 mb-2 mt-3">
                <label>NRIC</label>
                <input
                  type="text"
                  className="form-control"
                  name="nric"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.nric}
                />
              </div>
              <div className="col-md-6 col-12 mb-2 mt-3">
                <label>Address</label>
                <span className="text-danger">*</span>
                <input
                  type="text"
                  className="form-control"
                  name="userContractAddress"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.userContractAddress}
                  readOnly
                />
                {formik.touched.userContractAddress &&
                  formik.errors.userContractAddress && (
                    <div className="error text-danger ">
                      <small>{formik.errors.userContractAddress}</small>
                    </div>
                  )}
              </div>
              <div className="col-md-6 col-12 mb-2 mt-3">
                <label>Job Title</label>
                <input
                  type="text"
                  className="form-control"
                  name="jobTitle"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.jobTitle}
                />
              </div>
              <div className="col-md-6 col-12 mb-2 mt-3">
                <label>Main Duties</label>
                <input
                  type="text"
                  className="form-control"
                  name="mainDuties"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.mainDuties}
                />
              </div>
              <div className="col-md-6 col-12 mb-2 mt-3">
                <label>Start Date of Employment</label>
                <span className="text-danger">*</span>
                <input
                  type="date"
                  // onFocus={(e) => e.target.showPicker()}
                  className="form-control"
                  name="startDateOfEmployment"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.startDateOfEmployment}
                  min={new Date().toISOString().split("T")[0]}
                  readOnly
                />
                {formik.touched.startDateOfEmployment &&
                  formik.errors.startDateOfEmployment && (
                    <div className="error text-danger">
                      <small>{formik.errors.startDateOfEmployment}</small>
                    </div>
                  )}
              </div>
              <div className="col-md-6 col-12 mb-2 mt-3">
                <label>Training</label>
                <span className="text-danger">*</span>
                <input
                  type="text"
                  className="form-control"
                  name="training"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.training}
                />
                {formik.touched.training && formik.errors.training && (
                  <div className="error text-danger ">
                    <small>{formik.errors.training}</small>
                  </div>
                )}
              </div>
              <div className="col-md-6 col-12 mb-2 mt-3">
                <label>Allowance</label>
                <input
                  type="text"
                  className="form-control"
                  name="allowance"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.allowance}
                />
                {formik.touched.allowance && formik.errors.allowance && (
                  <div className="error text-danger ">
                    <small>{formik.errors.allowance}</small>
                  </div>
                )}
              </div>
              <div className="col-md-6 col-12 mb-2 mt-3">
                <label>Contract Start Date</label>
                <span className="text-danger">*</span>
                <CustomDatePicker
                  name="userContractStartDate"
                  value={formik.values.userContractStartDate}
                  onChange={(date) => {
                    formik.setFieldValue("userContractStartDate", date);
                    // Also update contract date if needed
                    if (!formik.values.contractDate) {
                      formik.setFieldValue("contractDate", date);
                    }
                  }}
                  onBlur={formik.handleBlur}
                  error={formik.errors.userContractStartDate}
                  touched={formik.touched.userContractStartDate}
                  readOnly={true} // Make it read-only as per requirement
                />
                {formik.touched.userContractStartDate &&
                  formik.errors.userContractStartDate && (
                    <div className="error text-danger">
                      <small>{formik.errors.userContractStartDate}</small>
                    </div>
                  )}
              </div>
              <div className="col-md-6 col-12 mb-2 mt-3">
                <label>Contract End Date</label>
                <CustomDatePicker
                  name="userContractEndDate"
                  value={formik.values.userContractEndDate}
                  onChange={(date) => {
                    formik.setFieldValue("userContractEndDate", date);
                    // Validate end date is after start date
                    if (
                      formik.values.userContractStartDate &&
                      date < new Date(formik.values.userContractStartDate)
                    ) {
                      formik.setFieldError(
                        "userContractEndDate",
                        "End date must be after start date"
                      );
                    } else {
                      formik.setFieldError("userContractEndDate", undefined);
                    }
                  }}
                  onBlur={formik.handleBlur}
                  error={formik.errors.userContractEndDate}
                  touched={formik.touched.userContractEndDate}
                  minDate={
                    formik.values.userContractStartDate
                      ? new Date(formik.values.userContractStartDate)
                      : null
                  }
                />
                {formik.touched.userContractEndDate &&
                  formik.errors.userContractEndDate && (
                    <div className="error text-danger">
                      <small>{formik.errors.userContractEndDate}</small>
                    </div>
                  )}
              </div>
              <div className="col-md-6 col-12 mb-2 mt-3">
                <label>Contract Period</label>
                <input
                  type="text"
                  className="form-control"
                  name="contactPeriod"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.contactPeriod}
                  readOnly
                />
              </div>
              <div className="col-md-6 col-12 mb-2 mt-3">
                <label>Probation (Day)</label>
                <input
                  type="text"
                  className="form-control"
                  name="probation"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.probation}
                />
              </div>
              <div className="col-md-6 col-12 mb-2 mt-3">
                <label>
                  Working Days<span className="text-danger">*</span>
                </label>
                <div className="mt-2 d-flex justify-content-between mt-3">
                  {[
                    "MONDAY",
                    "TUESDAY",
                    "WEDNESDAY",
                    "THURSDAY",
                    "FRIDAY",
                    "SATURDAY",
                    "SUNDAY",
                  ].map((day, index) => (
                    <div className="checkbox-container" key={day}>
                      <input
                        type="checkbox"
                        className="form-check-input"
                        id={`myCheckbox${index + 1}`}
                        value={day}
                        name="workingDays"
                        checked={
                          formik.values.workingDays &&
                          formik.values.workingDays.includes(day)
                        }
                        onChange={(e) => {
                          formik.handleChange(e);
                        }}
                        onBlur={formik.handleBlur}
                        disabled={workingDays && workingDays.length > 0}
                      />
                      <label
                        htmlFor={`myCheckbox${index + 1}`}
                        className="custom-checkbox"
                      >
                        <div className="inner-square"></div>
                      </label>
                      <label
                        htmlFor={`myCheckbox${index + 1}`}
                        className="mx-1"
                      >
                        {day.slice(0, 3)}
                      </label>
                    </div>
                  ))}
                </div>
                {formik.touched.workingDays && formik.errors.workingDays && (
                  <div className="error text-danger">
                    <small>{formik.errors.workingDays}</small>
                  </div>
                )}
              </div>

              <div className="col-md-6 col-12 mb-2 mt-3">
                <label>Salary</label>
                <span className="text-danger">*</span>
                <input
                  type="text"
                  className="form-control"
                  name="userContractSalary"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.userContractSalary}
                  readOnly
                />
                {formik.touched.userContractSalary &&
                  formik.errors.userContractSalary && (
                    <div className="error text-danger ">
                      <small>{formik.errors.userContractSalary}</small>
                    </div>
                  )}
              </div>
              <div className="col-md-6 col-12 mb-2 mt-3">
                <label>Salary Start Date</label>
                <input
                  type="date"
                  // onFocus={(e) => e.target.showPicker()}
                  className="form-control"
                  name="salaryStartDate"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.salaryStartDate}
                  min={new Date().toISOString().split("T")[0]}
                />
              </div>

              <div className="row mt-3">
                <span className="mt-3 fw-bold">Bank Account Details</span>
                <div className="col-md-6 col-12 mb-2 mt-3">
                  <label>Pay Now</label>
                  <input
                    type="text"
                    className="form-control"
                    name="payNow"
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.payNow}
                  />
                </div>
                <div className="col-md-6 col-12 mb-2 mt-3">
                  <label>Internet Banking</label>
                  <input
                    type="text"
                    className="form-control"
                    name="internetBanking"
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.internetBanking}
                  />
                </div>
                <div className="col-md-6 col-12 mb-2 mt-3">
                  <label>Contract Date</label>
                  <input
                    type="date"
                    // onFocus={(e) => e.target.showPicker()}
                    className="form-control"
                    name="contractDate"
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.contractDate}
                  />
                  {formik.touched.contractDate &&
                    formik.errors.contractDate && (
                      <div className="error text-danger ">
                        <small>{formik.errors.contractDate}</small>
                      </div>
                    )}
                </div>
                <div className="col-md-6 col-12 mb-2 mt-3">
                  <label>Termination Notice (Month)</label>
                  <input
                    type="text"
                    className="form-control"
                    name="terminationNotice"
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.terminationNotice}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </form>
    );
  }
);

export default StaffContractEdit;
